import React, { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { MathCell } from "@components/spreadsheet/cells/MathCell";
import {
    BaseCellProps,
    CategoryCell,
    CellType,
} from "@components/spreadsheet/cells";
import { ColumnDef } from "@components/spreadsheet/types";
import { TransactionSplitClassCell } from "@components/transactions/TransactionSplitV2/TransactionSplitClassCell";
import { TransactionSplitCounterpartyCell } from "@components/transactions/TransactionSplitV2/TransactionSplitCounterpartyCell";
import { createLedgerColumn } from "@components/transactions/TransactionSplitV2/createLedgerColumn";
import { SplitRuleScheduleCell } from "./SplitRuleScheduleCell";
import { SplitScheduleHoldingPatch } from "./resolveSplitScheduleHolding";
import styles from "./RuleSplitSpreadsheet.module.scss";

export interface SplitRow {
    _idx: number;
    splitDescription?: string | null;
    amount?: number | null;
    percentageAmount?: number | null;
    categoryId?: string;
    entityId?: number;
    classIds?: string[];
    counterpartyId?: string | null;
    accountId?: string | null;
    accrualAccountId?: string | null;
    scheduleTemplateId?: string | null;
}

interface UseSplitRuleSpreadsheetColumnsParams {
    entities: Entity[];
    isPercentage: boolean;
    isGlFirst: boolean;
    hasClasses: boolean;
    showAccrualColumn: boolean;
    showScheduleColumn: boolean;
    counterpartiesMap: Map<string, string>;
    splitsLength: number;
    schedulePatchByRowIdxRef: React.MutableRefObject<
        Map<number, SplitScheduleHoldingPatch>
    >;
}

function rowEntityId(row: Record<string, unknown>): number | undefined {
    return typeof row.entityId === "number" ? row.entityId : undefined;
}

function stringOrNull(value: BaseCellProps["value"]): string | null {
    return typeof value === "string" ? value : null;
}

export function useSplitRuleSpreadsheetColumns({
    entities,
    isPercentage,
    isGlFirst,
    hasClasses,
    showAccrualColumn,
    showScheduleColumn,
    counterpartiesMap,
    splitsLength,
    schedulePatchByRowIdxRef,
}: UseSplitRuleSpreadsheetColumnsParams): {
    columns: ColumnDef[];
    gridTemplateColumns: string;
} {
    const amountField = isPercentage ? "percentageAmount" : "amount";

    const gridTemplateColumns = useMemo(() => {
        const parts = ["40px", "1fr", "165px"];
        if (!isGlFirst) {
            parts.push("220px");
        }
        if (hasClasses) {
            parts.push("100px");
        }
        parts.push("180px", "220px", "200px");
        if (showAccrualColumn) {
            parts.push("200px");
        }
        if (showScheduleColumn) {
            parts.push("180px");
        }
        return parts.join(" ");
    }, [hasClasses, isGlFirst, showAccrualColumn, showScheduleColumn]);

    const columns: ColumnDef[] = useMemo(
        () => [
            { field: "", label: "", cellType: CellType.ROW_NUMBER },
            {
                field: "splitDescription",
                label: "Description",
                cellType: CellType.STRING,
                placeholder: "Add description…",
            },
            {
                field: amountField,
                label: isPercentage ? "%" : "Amount",
                cellType: CellType.MATH,
                ...(isPercentage
                    ? {}
                    : {
                          isMonetary: true,
                          cellFn: (props: BaseCellProps) =>
                              props.rowIdx === splitsLength - 1 ? (
                                  <span className={styles.netLabel}>Net</span>
                              ) : (
                                  <MathCell
                                      {...props}
                                      value={
                                          typeof props.value === "number"
                                              ? props.value
                                              : null
                                      }
                                      isMonetary
                                  />
                              ),
                      }),
            },
            ...(!isGlFirst
                ? [
                      {
                          field: "categoryId",
                          label: "Category",
                          cellType: CellType.CATEGORY,
                          cellFn: (props: BaseCellProps) => (
                              <CategoryCell
                                  {...props}
                                  value={stringOrNull(props.value)}
                              />
                          ),
                      },
                  ]
                : []),
            ...(hasClasses
                ? [
                      {
                          field: "classIds",
                          label: "Class",
                          cellType: CellType.CLASS,
                          cellFn: (props: BaseCellProps) => (
                              <TransactionSplitClassCell
                                  {...props}
                                  value={
                                      Array.isArray(props.value)
                                          ? props.value.filter(
                                                (id): id is string =>
                                                    typeof id === "string",
                                            )
                                          : null
                                  }
                              />
                          ),
                      },
                  ]
                : []),
            {
                field: "counterpartyId",
                label: "Counterparty",
                cellType: CellType.COUNTERPARTY,
                cellFn: (props) => (
                    <TransactionSplitCounterpartyCell
                        {...props}
                        value={stringOrNull(props.value)}
                        counterpartiesMap={counterpartiesMap}
                    />
                ),
            },
            {
                field: "entityId",
                label: "Entity",
                cellType: CellType.ENTITY,
                entities,
            },
            createLedgerColumn({
                ledgerBasis: AccountingLedgerBasis.cash,
                getEntityId: rowEntityId,
                canClear: !isGlFirst,
            }),
            ...(showAccrualColumn
                ? [
                      createLedgerColumn({
                          ledgerBasis: AccountingLedgerBasis.accruals,
                          getEntityId: rowEntityId,
                          canClear: true,
                      }),
                  ]
                : []),
            ...(showScheduleColumn
                ? [
                      {
                          field: "scheduleTemplateId",
                          label: "Schedule",
                          cellType: CellType.STRING,
                          cellFn: (props: BaseCellProps) => (
                              <SplitRuleScheduleCell
                                  {...props}
                                  value={stringOrNull(props.value)}
                                  entityId={rowEntityId(props.row)}
                                  schedulePatchByRowIdxRef={
                                      schedulePatchByRowIdxRef
                                  }
                              />
                          ),
                      },
                  ]
                : []),
        ],
        [
            amountField,
            counterpartiesMap,
            entities,
            hasClasses,
            isGlFirst,
            isPercentage,
            showAccrualColumn,
            showScheduleColumn,
            splitsLength,
            schedulePatchByRowIdxRef,
        ],
    );

    return { columns, gridTemplateColumns };
}
